import { AutomaticAction, GridColumn, hasCreationProcessQueryRule, ScanType, Type } from '../types';
import { translateFn } from '../../../../../utils/translator.helper';
import { DetectionRuleType } from '../../../../customRulesGrid/types';
import { TemplateResult } from 'lit/development';
import { DefaultColumnKeys, IColumnType, IDataTableColumn } from '../../../../../ui/gzDataTable/types';
import { Feature } from '@nirvana/shared/constants';

export class GzRuleAutomaticActionsGridDataProvider {
  private actionPlatformFlags: {[type in Type]: Feature} = {
    [Type.ISOLATE]: Feature.PLATFORM_FEATURE_EDR_AUTOMATIC_ACTION_ISOLATE,
    [Type.COLLECT_INVESTIGATION_PACKAGE]: Feature.PLATFORM_FEATURE_EDR_AUTOMATIC_ACTION_COLLECT_INVESTIGATION_PACKAGE,
    [Type.ADD_TO_SANDBOX]: Feature.PLATFORM_FEATURE_EDR_AUTOMATIC_ACTION_ADD_TO_SANDBOX,
    [Type.KILL_PROCESS]: Feature.PLATFORM_FEATURE_EDR_AUTOMATIC_ACTION_KILL_PROCESS,
    [Type.SCAN]: Feature.PLATFORM_FEATURE_EDR_AUTOMATIC_ACTION_SCAN,
    [Type.QUARANTINE]: Feature.PLATFORM_FEATURE_EDR_AUTOMATIC_ACTION_QUARANTINE,
    [Type.RISK_SCAN]: Feature.PLATFORM_FEATURE_EDR_AUTOMATIC_ACTION_RISK_SCAN,
  };

  private actionAvailabilityCheckers: {[type in Type]?: () => boolean} = {
    [Type.COLLECT_INVESTIGATION_PACKAGE]: (): boolean => !this.isGZ,
    [Type.ADD_TO_SANDBOX]: (): boolean => [
      DetectionRuleType.PROCESS,
      DetectionRuleType.FILE,
    ].includes(this.rule.query?.type) || hasCreationProcessQueryRule(this.rule),
    [Type.KILL_PROCESS]: (): boolean => DetectionRuleType.PROCESS === this.rule.query?.type
      || hasCreationProcessQueryRule(this.rule),
    [Type.QUARANTINE]: (): boolean => [
      DetectionRuleType.PROCESS,
      DetectionRuleType.FILE,
    ].includes(this.rule.query?.type) || hasCreationProcessQueryRule(this.rule),
    [Type.RISK_SCAN]: (): boolean => !this.isGZ,
  };

  private allActions: AutomaticAction[];

  constructor(
    private readonly rule: any,
    private readonly isGZ: boolean,
    private readonly platformHasFeature: (feature: Feature) => boolean,
    private readonly columnRenderers: {
      [key in GridColumn]?: (content: unknown, column: IDataTableColumn, rowIndex: number) => TemplateResult
    }
  ) {
  }

  public getColumns(): IDataTableColumn[] {
    return [
      {
        key: DefaultColumnKeys.REORDER,
        header: '',
        columnType: IColumnType.DISPLAY,
      },
      {
        key: DefaultColumnKeys.MULTI_SELECT,
        header: '',
        columnType: IColumnType.DISPLAY,
      },
      {
        key: DefaultColumnKeys.PRIORITY,
        header: translateFn('Priority'),
        columnType: IColumnType.DISPLAY,
      },
      {
        key: GridColumn.TYPE,
        header: translateFn('Automatic action'),
        columnType: IColumnType.DISPLAY,
        renderer: this.columnRenderers[GridColumn.TYPE],
      },
      {
        key: GridColumn.WARNING,
        header: translateFn('Warning'),
        columnType: IColumnType.DISPLAY,
        renderer: this.columnRenderers[GridColumn.WARNING],
      },
      {
        key: GridColumn.DETAILS,
        header: translateFn('Details'),
        columnType: IColumnType.DISPLAY,
        renderer: this.columnRenderers[GridColumn.DETAILS],
      },
      {
        key: GridColumn.EDIT_SETTINGS,
        header: translateFn('Edit settings'),
        columnType: IColumnType.DISPLAY,
        renderer: this.columnRenderers[GridColumn.EDIT_SETTINGS],
      },
    ];
  }

  public getActions(): AutomaticAction[] {
    const actions: AutomaticAction[] = [], existingActionsByType: {[type in Type]?: true} = {};

    for (const action of this.rule.automaticActions ?? []) {
      this.addActionIfAvailable(action, actions, existingActionsByType);
    }

    // Consider actions which just became available after editing the rule.
    this.loadAllActions();

    for (const action of this.allActions) {
      if (!existingActionsByType[action.type]) {
        this.addActionIfAvailable(action, actions, existingActionsByType);
      }
    }

    return actions;
  }

  private addActionIfAvailable(
    action: AutomaticAction,
    actions: AutomaticAction[],
    existingActionsByType: {[type in Type]?: true}
  ): void {
    if (!this.platformHasFeature(this.actionPlatformFlags[action.type])
      || (this.actionAvailabilityCheckers[action.type] && !this.actionAvailabilityCheckers[action.type]?.call(this))
    ) {
      return;
    }

    actions.push(action);
    existingActionsByType[action.type] = true;
  }

  private loadAllActions(): void {
    if (this.allActions) {
      return;
    }

    this.allActions = [
      {
        type: Type.ISOLATE,
        enabled: false,
      },
      {
        type: Type.COLLECT_INVESTIGATION_PACKAGE,
        enabled: false,
      },
      {
        type: Type.ADD_TO_SANDBOX,
        enabled: false,
      },
      {
        type: Type.KILL_PROCESS,
        enabled: false,
        settings: {
          includeParent: false,
          includeChildren: false,
        },
      },
      {
        type: Type.SCAN,
        enabled: false,
        settings: {
          type: ScanType.QUICK,
        },
      },
      {
        type: Type.QUARANTINE,
        enabled: false,
        settings: {
          includeParentProcess: false,
          includeChildrenProcesses: false,
        },
      },
      {
        type: Type.RISK_SCAN,
        enabled: false,
      },
    ];
  }
}
